import React, { useState, useEffect } from "react";

const FALLBACK_PRICES = [
  {
    id: "1",
    title: "Rengøring 1 gang",
    price: "375 kr.",
    unit: "pr. time",
    description: "Enkeltstående og fleksibel rengøring efter behov uden binding.",
    badge: "Enkeltstående",
    order: 1,
  },
  {
    id: "2",
    title: "Rengøring på en fast aftale",
    price: "350 kr.",
    unit: "pr. time",
    description: "Regelmæssig rengøring på en fast aftale – f.eks. ved faste intervaller eller hvert lejerskift.",
    badge: "Fast aftale",
    order: 2,
  },
  {
    id: "3",
    title: "Hoved rengøring",
    price: "400 kr.",
    unit: "pr. time",
    description: "Ekstra grundig rengøring af hele sommerhuset fra top til bund før sæson eller ved behov.",
    badge: "Dybdegående",
    order: 3,
  },
  {
    id: "4",
    title: "Flytte rengøring",
    price: "400 kr.",
    unit: "pr. time",
    description: "Komplet og detaljeret slutrengøring i forbindelse med fraflytning, salg eller overdragelse.",
    badge: "Slutrengøring",
    order: 4,
  },
];

export default function CleaningPricingTable() {
  const [prices, setPrices] = useState(FALLBACK_PRICES);

  useEffect(() => {
    fetch("/_api/public/v2/content/rengoering_priser?sort=order&order=asc&limit=25")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.items && Array.isArray(data.items) && data.items.length > 0) {
          setPrices(data.items);
        }
      })
      .catch(() => {});
  }, []);

  const formatPriceDisplay = (priceStr, unitStr) => {
    if (!priceStr) return "–";
    const cleaned = String(priceStr).trim();
    const unit = unitStr ? String(unitStr).trim() : "pr. time";
    if (cleaned.toLowerCase().includes("pr. time") || cleaned.toLowerCase().includes("pr time")) {
      return cleaned;
    }
    return `${cleaned} ${unit}`;
  };

  return (
    <section id="priser" className="py-14 lg:py-20 bg-[#FAF7F2]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="mb-10 sm:mb-12">
          <div className="max-w-2xl text-left space-y-2">
            <div className="inline-flex items-center gap-2 bg-[#EADFCF] text-[#4B3C2D] px-3.5 py-1 rounded-full text-xs font-semibold">
              <i className="fa-solid fa-tag text-[#887055]" aria-hidden="true"></i>
              <span>Gennemskuelige priser</span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-heading text-[#2C2523]">
              Priser for rengøring
            </h2>
            <p className="text-base text-[#5C524C] leading-relaxed">
              Oversigt over vores faste timepriser for sommerhusrengøring, fast service samt grundig hoved- og flytterengøring.
            </p>
          </div>
        </div>

        {/* 2-KOLONNE OVERSIGT INSPIRERET AF HUNDEKLIP OG VASK */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          
          {/* Kolonne 1: Almindelig rengøring & Fast aftale */}
          <div className="bg-white/80 rounded-3xl p-6 sm:p-8 border border-[#EADFCF] shadow-xs space-y-6">
            <div className="border-b border-[#EADFCF] pb-3 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold font-heading text-[#2C2523]">
                  Almindelig sommerhusrengøring
                </h3>
                <p className="text-xs text-[#887055] mt-1">
                  Enkeltstående eller fast service ved lejerskift og løbende vedligeholdelse
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-[#EADFCF] text-[#6B5640] flex items-center justify-center text-base shrink-0">
                <i className="fa-solid fa-broom" aria-hidden="true"></i>
              </div>
            </div>

            <div className="divide-y divide-[#EADFCF]/70">
              {prices.slice(0, 2).map((item) => (
                <div key={item.id || item.title} className="py-4 first:pt-0 space-y-2">
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1 pr-2">
                      <div className="flex items-center gap-2">
                        <h4 className="text-base font-semibold text-[#2C2523] leading-snug">
                          {item.title}
                        </h4>
                        {item.badge && (
                          <span className="inline-block text-[10px] font-semibold text-[#6B5640] bg-[#EADFCF]/80 px-2 py-0.5 rounded-md">
                            {item.badge}
                          </span>
                        )}
                      </div>
                      {item.description && (
                        <p className="text-xs text-[#6B5D55] leading-relaxed">
                          {item.description}
                        </p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <span className="text-base sm:text-lg font-bold text-[#2C2523] block whitespace-nowrap">
                        {item.price}
                      </span>
                      <span className="text-xs text-[#887055] font-medium block">
                        {item.unit || "pr. time"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Kolonne 2: Hovedrengøring & Flytterengøring */}
          <div className="bg-white/80 rounded-3xl p-6 sm:p-8 border border-[#EADFCF] shadow-xs space-y-6">
            <div className="border-b border-[#EADFCF] pb-3 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold font-heading text-[#2C2523]">
                  Dybdegående & flytterengøring
                </h3>
                <p className="text-xs text-[#887055] mt-1">
                  Grundig og detaljeret rengøring før sæsonstart, salg eller fraflytning
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-[#EADFCF] text-[#6B5640] flex items-center justify-center text-base shrink-0">
                <i className="fa-solid fa-sparkles" aria-hidden="true"></i>
              </div>
            </div>

            <div className="divide-y divide-[#EADFCF]/70">
              {prices.slice(2, 4).map((item) => (
                <div key={item.id || item.title} className="py-4 first:pt-0 space-y-2">
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1 pr-2">
                      <div className="flex items-center gap-2">
                        <h4 className="text-base font-semibold text-[#2C2523] leading-snug">
                          {item.title}
                        </h4>
                        {item.badge && (
                          <span className="inline-block text-[10px] font-semibold text-[#6B5640] bg-[#EADFCF]/80 px-2 py-0.5 rounded-md">
                            {item.badge}
                          </span>
                        )}
                      </div>
                      {item.description && (
                        <p className="text-xs text-[#6B5D55] leading-relaxed">
                          {item.description}
                        </p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <span className="text-base sm:text-lg font-bold text-[#2C2523] block whitespace-nowrap">
                        {item.price}
                      </span>
                      <span className="text-xs text-[#887055] font-medium block">
                        {item.unit || "pr. time"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* Gyldigheds- og infoboks – matcher designet under hundeklip og vask */}
        <div className="mt-8 p-5 sm:p-6 bg-[#FAF7F2] rounded-2xl border border-[#EADFCF] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-xl bg-[#EADFCF] text-[#6B5640] flex items-center justify-center shrink-0 text-base">
              <i className="fa-solid fa-calendar-check" aria-hidden="true"></i>
            </div>
            <div>
              <p className="text-sm font-bold text-[#2C2523]">
                Alle priser gælder til 31 okt 2027
              </p>
              <p className="text-xs text-[#5C524C]">
                Vi medbringer selv professionelt udstyr og miljøvenlige rengøringsmidler til opgaven.
              </p>
            </div>
          </div>
          <a
            href="#bestil-form"
            className="inline-flex items-center gap-2 bg-[#EADFCF] hover:bg-[#D8C7B0] text-[#2C2523] px-5 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-colors border border-[#D8C7B0] shadow-2xs whitespace-nowrap"
          >
            <span>Bestil rengøring</span>
            <i className="fa-solid fa-arrow-down text-xs text-[#887055]" aria-hidden="true"></i>
          </a>
        </div>

      </div>
    </section>
  );
}
